import { useEffect, useRef, useState } from 'react'
import { jwtDecode } from 'jwt-decode'
import storage from 'utils/storage'

const IDLE_TIMEOUT_MS = 15 * 60 * 1000
const WARNING_MS = 60 * 1000
const ACTIVITY_WRITE_INTERVAL_MS = 5 * 1000

export default function useInactivityLogout({ enabled, logout }) {
  const [secondsRemaining, setSecondsRemaining] = useState(null)
  const continueRef = useRef(null)

  useEffect(() => {
    if (!enabled) {
      continueRef.current = null
      setSecondsRemaining(null)
      return undefined
    }

    const token = storage.getToken()
    if (!token) return undefined

    let claims
    try {
      claims = jwtDecode(token)
    } catch (_error) {
      logout()
      return undefined
    }

    const activityKey = `stk:session-activity:${claims.id}:${claims.iat}`
    const issuedAt = Number(claims.iat) * 1000
    const expiresAt = Number(claims.exp) * 1000
    let lastActivityAt = issuedAt
    let lastWriteAt = 0
    let warningOpen = false
    let loggingOut = false

    const readActivity = () => {
      try {
        const saved = Number(window.localStorage.getItem(activityKey))
        return Number.isFinite(saved) && saved >= issuedAt && saved <= Date.now() ? saved : null
      } catch (_error) {
        return null
      }
    }

    const writeActivity = (at) => {
      try {
        window.localStorage.setItem(activityKey, String(at))
        lastWriteAt = at
      } catch (_error) {
        // Private browsing can block storage; the current tab still tracks activity.
      }
    }

    const savedActivity = readActivity()
    if (savedActivity) {
      lastActivityAt = savedActivity
      lastWriteAt = savedActivity
    } else if (Date.now() - issuedAt < IDLE_TIMEOUT_MS) {
      lastActivityAt = Date.now()
      writeActivity(lastActivityAt)
    }

    const checkIdle = () => {
      if (loggingOut) return
      const sharedActivity = readActivity()
      if (sharedActivity && sharedActivity > lastActivityAt) {
        lastActivityAt = sharedActivity
        warningOpen = false
      }
      const now = Date.now()
      const idleRemainingMs = lastActivityAt + IDLE_TIMEOUT_MS - now
      const sessionRemainingMs = expiresAt - now
      if (idleRemainingMs <= 0 || sessionRemainingMs <= 0) {
        loggingOut = true
        setSecondsRemaining(null)
        logout()
      } else if (idleRemainingMs <= WARNING_MS && idleRemainingMs < sessionRemainingMs) {
        warningOpen = true
        setSecondsRemaining(Math.ceil(idleRemainingMs / 1000))
      } else {
        warningOpen = false
        setSecondsRemaining(null)
      }
    }

    const recordActivity = () => {
      if (warningOpen || loggingOut) return
      const now = Date.now()
      if (now >= expiresAt || now >= lastActivityAt + IDLE_TIMEOUT_MS) {
        checkIdle()
        return
      }
      lastActivityAt = now
      if (now - lastWriteAt >= ACTIVITY_WRITE_INTERVAL_MS) writeActivity(now)
    }

    continueRef.current = () => {
      if (!warningOpen || loggingOut) return
      const now = Date.now()
      if (now >= expiresAt || now >= lastActivityAt + IDLE_TIMEOUT_MS) {
        checkIdle()
        return
      }
      lastActivityAt = now
      warningOpen = false
      writeActivity(now)
      setSecondsRemaining(null)
    }

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') checkIdle()
    }
    const handleSharedActivity = (event) => {
      if (event.key === activityKey) checkIdle()
    }

    window.addEventListener('pointerdown', recordActivity, { passive: true })
    window.addEventListener('keydown', recordActivity)
    window.addEventListener('touchstart', recordActivity, { passive: true })
    window.addEventListener('storage', handleSharedActivity)
    document.addEventListener('visibilitychange', handleVisibility)
    const intervalId = window.setInterval(checkIdle, 1000)
    checkIdle()

    return () => {
      continueRef.current = null
      window.clearInterval(intervalId)
      window.removeEventListener('pointerdown', recordActivity)
      window.removeEventListener('keydown', recordActivity)
      window.removeEventListener('touchstart', recordActivity)
      window.removeEventListener('storage', handleSharedActivity)
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [enabled, logout])

  return { secondsRemaining, continueSession: () => continueRef.current?.() }
}
